import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtDate, fmtTime, payLabel } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { CreateAccountPanel, ResetPasswordControl } from "@/components/AdminAccountControls";

const Stat = ({ label, value, testId }) => (
  <div className="card-surface p-5" data-testid={testId}>
    <p className="overline">{label}</p>
    <p className="mt-2 font-display text-3xl font-semibold">{value}</p>
  </div>
);

export function AdminDashboard() {
  const [m, setM] = useState(null);
  useEffect(() => {
    (async () => setM((await api.get("/admin/metrics")).data))();
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Platform overview</h1>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Pharmacies" value={m?.total_pharmacies ?? "—"} testId="admin-stat-pharmacies" />
        <Stat label="Workers" value={m?.total_workers ?? "—"} testId="admin-stat-workers" />
        <Stat label="Pharmacists" value={m?.pharmacists ?? "—"} testId="admin-stat-pharmacists" />
        <Stat label="Technicians" value={m?.technicians ?? "—"} testId="admin-stat-technicians" />
        <Stat label="Clerks" value={m?.clerks ?? "—"} testId="admin-stat-clerks" />
        <Stat label="Open shifts" value={m?.open_shifts ?? "—"} testId="admin-stat-open" />
        <Stat label="Confirmed shifts" value={m?.confirmed_shifts ?? "—"} testId="admin-stat-confirmed" />
        <Stat label="Completed shifts" value={m?.completed_shifts ?? "—"} testId="admin-stat-completed" />
        <Stat label="Cancelled shifts" value={m?.cancelled_shifts ?? "—"} testId="admin-stat-cancelled" />
        <Stat label="Cancellation rate" value={m ? `${m.cancellation_rate}%` : "—"} testId="admin-stat-cancel-rate" />
        <Stat label="Interests" value={m?.total_interests ?? "—"} testId="admin-stat-interests" />
        <Stat label="Requests" value={m?.total_requests ?? "—"} testId="admin-stat-requests" />
        <Stat label="Open reports" value={m?.open_reports ?? "—"} testId="admin-stat-open-reports" />
        <Stat label="Consent records" value={m?.consent_records ?? "—"} testId="admin-stat-consents" />
      </div>
    </div>
  );
}

export const DeleteAccountControl = ({ userId, email, onDeleted, testIdSuffix }) => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const remove = async () => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/admin/users/${userId}`);
      setOpen(false);
      await onDeleted();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTyped(""); setError(""); }}
        className="text-sm font-medium text-red-600 hover:underline"
        data-testid={`delete-account-${testIdSuffix}`}
      >
        Delete
      </button>
    );
  }

  return (
    <div className="min-w-[15rem] space-y-2 rounded-xl border border-red-200 bg-red-50 p-3" data-testid={`delete-confirm-${testIdSuffix}`}>
      <p className="text-xs font-medium text-red-800">
        Permanently deletes this account, its profile and its shifts. Type <strong>{email}</strong> to confirm.
      </p>
      <input
        className="field-input py-1.5 text-xs"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={email}
        data-testid={`delete-confirm-input-${testIdSuffix}`}
      />
      {error && <p className="text-xs text-red-700" data-testid={`delete-error-${testIdSuffix}`}>{error}</p>}
      <div className="flex gap-2">
        <button
          onClick={remove}
          disabled={busy || typed.trim().toLowerCase() !== (email || "").toLowerCase()}
          className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
          data-testid={`delete-submit-${testIdSuffix}`}
        >
          {busy ? "Deleting…" : "Delete account"}
        </button>
        <button onClick={() => setOpen(false)} className="text-xs font-medium text-slate-600" data-testid={`delete-cancel-${testIdSuffix}`}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const Search = ({ value, onChange, testId }) => (
  <input
    className="field-input mt-6 max-w-sm"
    placeholder="Search…"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
  />
);

export function AdminAccounts() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setRows((await api.get("/admin/accounts", { params: q ? { q } : {} })).data);
  }, [q]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-accounts">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">All accounts</h1>
      <p className="mt-2 text-sm text-slate-600">
        Every registered account, including sign-ups that never finished onboarding and so do not appear
        in the Workers or Pharmacies tables.
      </p>
      <Search value={q} onChange={setQ} testId="admin-accounts-search" />
      <div className="mt-4 flex flex-wrap items-start gap-3">
        <CreateAccountPanel onCreated={load} />
        <button
        type="button"
        onClick={async () => {
          setError("");
          try {
            const { data } = await api.post("/admin/cleanup-demo-data");
            setError(`Cleanup done — ${data.demo_accounts_removed} demo accounts removed.`);
            load();
          } catch (e) {
            setError(apiError(e));
          }
        }}
        className="btn-ghost"
        data-testid="cleanup-demo-data"
      >
        Remove demo &amp; orphaned data
      </button>
      </div>
      {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="card-surface mt-6 overflow-x-auto">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">Account</th>
              <th className="overline">Role</th>
              <th className="overline">Onboarding</th>
              <th className="overline">Status</th>
              <th className="overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-account-${u.user_id}`}>
                <td className="px-5 py-4">
                  <p className="font-medium">{u.name || "—"}</p>
                  <p className="text-xs text-slate-400">{u.email}</p>
                </td>
                <td className="px-5 py-4 capitalize text-slate-600">{u.role || "not chosen"}</td>
                <td className="px-5 py-4">
                  {u.role === "admin" ? (
                    <span className="text-slate-400">Not applicable</span>
                  ) : u.has_profile ? (
                    <span className="pill border-emerald-200 bg-emerald-50 text-emerald-700">
                      Profile{u.npi_verified ? " · NPI verified" : ""}
                    </span>
                  ) : (
                    <span className="pill border-amber-200 bg-amber-50 text-amber-800" data-testid={`incomplete-${u.user_id}`}>
                      Incomplete
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-slate-600">{u.suspended ? "Suspended" : u.verification_status}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap items-start gap-3">
                    {u.role !== "admin" && (
                      <>
                        <button
                          onClick={async () => {
                            setError("");
                            try {
                              await api.post(`/admin/users/${u.user_id}/suspend`, { status: u.suspended ? "activate" : "suspend" });
                              load();
                            } catch (e) {
                              setError(apiError(e));
                            }
                          }}
                          className="text-sm font-medium text-brand"
                          data-testid={`account-suspend-${u.user_id}`}
                        >
                          {u.suspended ? "Reactivate" : "Suspend"}
                        </button>
                        <DeleteAccountControl userId={u.user_id} email={u.email} onDeleted={load} testIdSuffix={u.user_id} />
                      </>
                    )}
                    <ResetPasswordControl userId={u.user_id} email={u.email} testIdSuffix={u.user_id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminWorkers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setRows((await api.get("/admin/workers", { params: q ? { q } : {} })).data);
  }, [q]);
  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn) => {
    setError("");
    try {
      await fn();
      await load();
    } catch (e) {
      setError(apiError(e));
    }
  };

  return (
    <div data-testid="admin-workers">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Workers</h1>
      <p className="mt-2 text-sm text-slate-600">
        Verification is automatic: a worker becomes <strong>Pending verification</strong> when their first
        shift is confirmed, and <strong>Verified</strong> once a pharmacy reviews a completed shift.
      </p>
      <Search value={q} onChange={setQ} testId="admin-workers-search" />
      {error && <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      <div className="card-surface mt-6 overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">Name</th>
              <th className="overline">Roles</th>
              <th className="overline">ZIP</th>
              <th className="overline">Min rate</th>
              <th className="overline">Verification</th>
              <th className="overline">Activity</th>
              <th className="overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.user_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-worker-${w.user_id}`}>
                <td className="px-5 py-4">
                  <p className="font-medium">{w.name}</p>
                  <p className="text-xs text-slate-400">{w.email}</p>
                </td>
                <td className="px-5 py-4 text-slate-600">{w.profile.worker_roles.join(", ")}</td>
                <td className="px-5 py-4">{w.profile.zip_code}</td>
                <td className="px-5 py-4">${w.profile.minimum_rate}/hr</td>
                <td className="px-5 py-4">
                  <span className="pill border-slate-200 bg-slate-50 text-slate-600" data-testid={`verification-${w.user_id}`}>
                    {w.verification_status}
                  </span>
                  <p className="mt-1 text-xs text-slate-400">Automatic</p>
                </td>
                <td className="px-5 py-4 text-slate-600">
                  {w.interests} interests · {w.confirmed} confirmed
                </td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <button
                      onClick={() => act(() => api.post(`/admin/users/${w.user_id}/suspend`, { status: w.suspended ? "activate" : "suspend" }))}
                      className="text-sm font-medium text-brand"
                      data-testid={`suspend-${w.user_id}`}
                    >
                      {w.suspended ? "Reactivate" : "Suspend"}
                    </button>
                    <DeleteAccountControl userId={w.user_id} email={w.email} onDeleted={load} testIdSuffix={w.user_id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminPharmacies() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setRows((await api.get("/admin/pharmacies", { params: q ? { q } : {} })).data);
  }, [q]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-pharmacies">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Pharmacies</h1>
      <Search value={q} onChange={setQ} testId="admin-pharmacies-search" />
      <div className="card-surface mt-6 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">Pharmacy</th>
              <th className="overline">Type</th>
              <th className="overline">Location</th>
              <th className="overline">Software</th>
              <th className="overline">Shifts</th>
              <th className="overline">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.pharmacy_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-pharmacy-${p.pharmacy_id}`}>
                <td className="px-5 py-4">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-slate-400">{p.user?.email}</p>
                  {p.npi_verified && p.npi ? (
                    <span className="pill mt-1 border-emerald-200 bg-emerald-50 text-emerald-700" data-testid={`npi-verified-${p.pharmacy_id}`}>
                      NPI {p.npi}
                    </span>
                  ) : (
                    <span className="pill mt-1 border-slate-200 bg-slate-50 text-slate-500" data-testid={`npi-missing-${p.pharmacy_id}`}>
                      No verified NPI
                    </span>
                  )}
                </td>
                <td className="px-5 py-4">{p.pharmacy_type}</td>
                <td className="px-5 py-4 text-slate-600">
                  {p.city}, {p.state} {p.zip_code}
                </td>
                <td className="px-5 py-4 text-slate-600">{(p.software || []).join(", ")}</td>
                <td className="px-5 py-4">{p.shift_count}</td>
                <td className="px-5 py-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <button
                      onClick={async () => {
                        await api.post(`/admin/users/${p.user_id}/suspend`, { status: p.user?.suspended ? "activate" : "suspend" });
                        load();
                      }}
                      className="text-sm font-medium text-brand"
                      data-testid={`suspend-pharmacy-${p.pharmacy_id}`}
                    >
                      {p.user?.suspended ? "Reactivate" : "Suspend"}
                    </button>
                    <DeleteAccountControl userId={p.user_id} email={p.user?.email} onDeleted={load} testIdSuffix={p.pharmacy_id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminShifts() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (status) params.status = status;
    if (q) params.q = q;
    setRows((await api.get("/admin/shifts", { params })).data);
  }, [status, q]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="admin-shifts">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Shifts</h1>
      <div className="mt-6 flex flex-wrap gap-3">
        <input className="field-input max-w-sm" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-shifts-search" />
        <select className="field-input max-w-xs" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-shifts-status-filter">
          <option value="">All statuses</option>
          {["Open", "Interested", "Requested", "Confirmed", "Completed", "Cancelled"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>
      <div className="card-surface mt-6 overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">Date</th>
              <th className="overline">Role</th>
              <th className="overline">Pharmacy</th>
              <th className="overline">Location</th>
              <th className="overline">Time</th>
              <th className="overline">Pay</th>
              <th className="overline">Interested</th>
              <th className="overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.shift_id} className="border-b border-slate-100 last:border-0" data-testid={`admin-shift-${s.shift_id}`}>
                <td className="px-5 py-4 font-medium">{fmtDate(s.date, { month: "short", day: "numeric", year: "numeric" })}</td>
                <td className="px-5 py-4">{s.worker_role}</td>
                <td className="px-5 py-4">{s.pharmacy?.name}</td>
                <td className="px-5 py-4 text-slate-600">{s.city}</td>
                <td className="px-5 py-4">{fmtTime(s.start_time)} – {fmtTime(s.end_time)}</td>
                <td className="px-5 py-4 font-display font-semibold">{payLabel(s)}</td>
                <td className="px-5 py-4">{s.interest_count}</td>
                <td className="px-5 py-4">
                  <select
                    className="field-input py-1.5"
                    value={s.status}
                    onChange={async (e) => {
                      await api.post(`/admin/shifts/${s.shift_id}/status`, { status: e.target.value });
                      load();
                    }}
                    data-testid={`admin-shift-status-${s.shift_id}`}
                  >
                    {["Open", "Interested", "Requested", "Confirmed", "Completed", "Cancelled"].map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminMatches() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    (async () => setRows((await api.get("/admin/matches")).data))();
  }, []);

  return (
    <div data-testid="admin-matches">
      <p className="overline">Admin</p>
      <h1 className="mt-3 font-display text-3xl font-semibold tracking-tight sm:text-4xl">Matches & requests</h1>
      <div className="card-surface mt-8 overflow-x-auto">
        <table className="w-full min-w-[820px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/60 text-left">
            <tr className="[&>th]:px-5 [&>th]:py-3">
              <th className="overline">Type</th>
              <th className="overline">Worker</th>
              <th className="overline">Pharmacy</th>
              <th className="overline">Shift</th>
              <th className="overline">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}-${r.created_at}-${r.worker_name}`} className="border-b border-slate-100 last:border-0" data-testid={`admin-match-${r.created_at}`}>
                <td className="px-5 py-4 capitalize">{r.kind}</td>
                <td className="px-5 py-4">{r.worker_name}</td>
                <td className="px-5 py-4">{r.pharmacy_name}</td>
                <td className="px-5 py-4 text-slate-600">
                  {r.shift ? `${r.shift.worker_role} · ${fmtDate(r.shift.date)} · ${fmtTime(r.shift.start_time)}` : "—"}
                </td>
                <td className="px-5 py-4 capitalize">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
